import {
  Bone,
  CardHeaderSkeleton,
  CardSkeleton,
  FieldSkeleton,
  HeaderSkeleton,
  Line,
  LinkTabsSkeleton,
  MeterSkeleton,
  PanelSkeleton,
  pick,
  placeholderKeys,
  SkeletonRegion,
  TableSkeleton,
} from '@ems/ui/patterns/skeleton-blocks'

const METER_KEYS = placeholderKeys('meter', 4)
const BADGE_KEYS = placeholderKeys('badge', 3)
const CARD_KEYS = placeholderKeys('card', 6)
const MODULE_KEYS = placeholderKeys('module', 6)
const DL_KEYS = placeholderKeys('dl', 4)
const NAME_WIDTHS = ['w-32', 'w-40', 'w-28', 'w-36'] as const

function Meters() {
  return (
    <div className="grid gap-3">
      {METER_KEYS.map((key) => (
        <MeterSkeleton key={key} />
      ))}
    </div>
  )
}

function Badges({ className }: { className: string }) {
  return (
    <div className={className}>
      {BADGE_KEYS.map((key, at) => (
        <Bone key={key} className={`h-[22px] ${pick(['w-16', 'w-20', 'w-24'], at)}`} />
      ))}
    </div>
  )
}

export function TenantsSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-24" description="w-80" actions={['h-9 w-40']} />
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <Bone className="h-9 w-full sm:max-w-xs" />
          <div className="grid grid-cols-[1.35fr_1fr_1fr] gap-2 sm:contents">
            <Bone className="h-9 w-full sm:w-40" />
            <Bone className="h-9 w-full sm:w-44" />
            <Bone className="h-9 w-full sm:w-44" />
          </div>
        </div>
        <TableSkeleton
          rows={8}
          card={{ title: 'w-36', description: 'w-72' }}
          columns={[
            { cell: 'two-line', widths: ['w-40', 'w-24', 'w-32', 'w-20'], primary: true },
            { cell: 'mono', widths: ['w-40', 'w-32'] },
            { cell: 'badge', widths: ['w-16', 'w-20'] },
            { cell: 'text', widths: ['w-20', 'w-24'] },
            { cell: 'muted', widths: ['w-16', 'w-24'] },
            { cell: 'mono', widths: ['w-16'], header: 'w-28' },
            { cell: 'icon', header: null, align: 'end', shrink: true },
          ]}
        />
      </div>
    </SkeletonRegion>
  )
}

export function TenantSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <Line width="w-20" />
        <HeaderSkeleton title="w-56" description="w-64" actions={['h-9 w-24']} />
        <Badges className="flex flex-wrap gap-1.5" />
        <LinkTabsSkeleton count={5} className="border-b" />
      </div>
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <CardSkeleton>
            <CardHeaderSkeleton title="w-20" description="w-48" action="h-8 w-14" />
            <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 px-6">
              {DL_KEYS.map((key, at) => (
                <div key={key} className="contents">
                  <Line width="w-20" />
                  <Line width={pick(NAME_WIDTHS, at)} />
                </div>
              ))}
            </div>
          </CardSkeleton>
        </div>
        <div className="flex flex-col gap-4">
          <CardSkeleton>
            <CardHeaderSkeleton title="w-20" description="w-40" action="h-8 w-20" />
            <div className="flex flex-col gap-4 px-6">
              <Bone className="h-16 w-full rounded-lg" />
              <Badges className="flex flex-wrap gap-1.5" />
            </div>
          </CardSkeleton>
        </div>
      </div>
    </SkeletonRegion>
  )
}

export function TenantUsageSkeleton() {
  return (
    <SkeletonRegion className="grid gap-4 lg:grid-cols-2">
      <CardSkeleton>
        <CardHeaderSkeleton title="w-20" />
        <div className="px-6">
          <Meters />
        </div>
      </CardSkeleton>
      <CardSkeleton>
        <CardHeaderSkeleton title="w-28" />
        <div className="grid grid-cols-[1fr_auto] gap-y-2 px-6">
          {BADGE_KEYS.map((key) => (
            <div key={key} className="contents">
              <Line width="w-32" />
              <Line width="w-10" />
            </div>
          ))}
        </div>
      </CardSkeleton>
    </SkeletonRegion>
  )
}

export function PlansSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-20" description="w-80" actions={['h-9 w-28']} />
      <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {CARD_KEYS.slice(0, 3).map((key, at) => (
          <li key={key}>
            <CardSkeleton className="h-full">
              <CardHeaderSkeleton title={pick(NAME_WIDTHS, at)} description="w-40" />
              <div className="flex flex-col gap-3 px-6">
                <Badges className="flex flex-wrap gap-1.5" />
                <div className="grid gap-y-1">
                  {DL_KEYS.map((row) => (
                    <div key={row} className="flex justify-between gap-2">
                      <Line size="xs" width="w-24" />
                      <Line size="xs" width="w-12" />
                    </div>
                  ))}
                </div>
              </div>
            </CardSkeleton>
          </li>
        ))}
      </ul>
    </SkeletonRegion>
  )
}

function ModuleRows() {
  return (
    <ul className="divide-border flex flex-col divide-y rounded-lg border">
      {MODULE_KEYS.map((key, at) => (
        <li
          key={key}
          className="flex flex-col gap-3 px-4 py-3 md:flex-row md:items-center md:justify-between"
        >
          <div className="flex flex-col gap-1">
            <Line width={pick(NAME_WIDTHS, at)} />
            <Line size="xs" width="w-56" />
          </div>
          <Bone className="h-8 w-60" />
        </li>
      ))}
    </ul>
  )
}

export function PlanSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <Line width="w-16" />
        <HeaderSkeleton title="w-40" description="w-48" actions={['h-9 w-32']} />
      </div>
      <div className="flex flex-col gap-4">
        <div className="grid gap-4 lg:grid-cols-2">
          <CardSkeleton>
            <CardHeaderSkeleton title="w-16" />
            <div className="flex flex-col gap-4 px-6">
              <FieldSkeleton label="w-12" />
              <FieldSkeleton label="w-24" control="textarea-sm" />
              <Bone className="h-16 w-full" />
            </div>
          </CardSkeleton>
          <CardSkeleton>
            <CardHeaderSkeleton title="w-16" />
            <div className="grid gap-4 px-6 sm:grid-cols-2">
              {DL_KEYS.map((key) => (
                <FieldSkeleton key={key} label="w-28" />
              ))}
            </div>
          </CardSkeleton>
        </div>
        <CardSkeleton>
          <CardHeaderSkeleton title="w-48" />
          <div className="grid gap-2 px-6 sm:grid-cols-2 xl:grid-cols-3">
            {BADGE_KEYS.map((key) => (
              <Bone key={key} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        </CardSkeleton>
        <div className="flex flex-col gap-3">
          <Line size="base" width="w-20" />
          <Line width="w-80" />
          <ModuleRows />
        </div>
      </div>
    </SkeletonRegion>
  )
}

export function SeriesSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-20" description="w-80" actions={['h-9 w-28']} />
      <TableSkeleton
        rows={4}
        columns={[
          { cell: 'text', widths: ['w-40', 'w-32'], primary: true },
          { cell: 'mono', widths: ['w-24', 'w-20'] },
          { cell: 'text', widths: ['w-6'], align: 'end' },
          { cell: 'button', widths: ['w-16'], header: null, align: 'end', shrink: true },
        ]}
      />
    </SkeletonRegion>
  )
}

export function SeriesDetailSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <Line width="w-16" />
        <HeaderSkeleton title="w-40" description="w-32" />
      </div>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {METER_KEYS.map((key) => (
          <Bone key={key} className="h-24 w-full rounded-xl" />
        ))}
      </div>
      <TableSkeleton
        rows={5}
        columns={[
          { cell: 'text', widths: ['w-36', 'w-28'], primary: true },
          { cell: 'text', widths: ['w-16'], align: 'end' },
          { cell: 'text', widths: ['w-16'], align: 'end' },
          { cell: 'text', widths: ['w-12'], align: 'end' },
        ]}
      />
    </SkeletonRegion>
  )
}

export function UsageSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-20" description="w-80" />
      <ul className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {CARD_KEYS.map((key, at) => (
          <li key={key}>
            <CardSkeleton className="h-full">
              <CardHeaderSkeleton title={pick(NAME_WIDTHS, at)} description="w-36" />
              <div className="px-6">
                <Meters />
              </div>
            </CardSkeleton>
          </li>
        ))}
      </ul>
    </SkeletonRegion>
  )
}

export function UsersSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-20" description="w-72" />
      <div className="flex gap-2">
        <Bone className="h-9 w-full max-w-md" />
        <Bone className="h-9 w-20" />
      </div>
      <TableSkeleton
        rows={8}
        columns={[
          { cell: 'two-line', widths: ['w-48', 'w-24', 'w-40', 'w-32'], primary: true },
          { cell: 'badge', widths: ['w-16', 'w-20'] },
          { cell: 'text', widths: ['w-6'], align: 'end' },
          { cell: 'muted', widths: ['w-28', 'w-24'] },
        ]}
      />
      <Bone className="h-9 w-28 self-center" />
    </SkeletonRegion>
  )
}

export function UserSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-8">
      <div>
        <Line width="w-16" />
        <HeaderSkeleton title="w-64" description="w-56" className="mt-1" />
        <Badges className="mt-3 flex flex-wrap gap-1" />
      </div>
      <PanelSkeleton title="w-20" description="w-80" className="gap-3">
        <div className="flex flex-wrap gap-2">
          <Bone className="h-9 w-32" />
          <Bone className="h-9 w-28" />
          <Bone className="h-9 w-24" />
        </div>
      </PanelSkeleton>
      <TableSkeleton
        rows={3}
        card={{ title: 'w-20', description: 'w-72', action: 'h-8 w-32' }}
        columns={[
          { cell: 'two-line', widths: ['w-40', 'w-24'], primary: true },
          { cell: 'select' },
          { cell: 'muted', widths: ['w-24'] },
          { cell: 'button', widths: ['w-20'], header: null, align: 'end', shrink: true },
        ]}
      />
      <TableSkeleton
        rows={3}
        card={{ title: 'w-20', description: 'w-72' }}
        columns={[
          { cell: 'text', widths: ['w-32'], primary: true },
          { cell: 'text', widths: ['w-32'] },
          { cell: 'mono', widths: ['w-28', 'w-24'] },
          { cell: 'badge', widths: ['w-16'] },
        ]}
      />
      <PanelSkeleton title="w-44" className="gap-3">
        <Line width="w-80" />
        <Line width="w-64" />
      </PanelSkeleton>
    </SkeletonRegion>
  )
}

/** The payment method catalog: a header with one action, and a short table. */
export function CatalogSkeleton() {
  return (
    <SkeletonRegion className="flex flex-col gap-6">
      <HeaderSkeleton title="w-40" description="w-96" actions={['h-9 w-44']} />
      <TableSkeleton
        rows={3}
        columns={[
          { cell: 'text', widths: ['w-32', 'w-28'], primary: true },
          { cell: 'mono', widths: ['w-28', 'w-24'] },
          { cell: 'badge', widths: ['w-16'] },
          { cell: 'text', widths: ['w-24', 'w-20'] },
          { cell: 'text', widths: ['w-10'] },
          { cell: 'button', widths: ['w-32'], header: null, align: 'end', shrink: true },
        ]}
      />
    </SkeletonRegion>
  )
}
